import { Alert, Box, Link, SpaceBetween, TextContent } from "@amzn/awsui-components-console";
import React from "react";

import { ROUTES } from "src/app/constants";
import { LOB_LABELS } from "src/jsonForms/preScreen/lobAccess";
import type { LobKey } from "src/jsonForms/preScreen/types";

import type { SearchConfigFailure } from "./searchConfigResolver";

export interface ConfigErrorPanelProps {
  readonly failure: SearchConfigFailure;
}

function unknownRouteMessage(pathname: string): string {
  const intro = `No line of business is registered for the path "${pathname}".`;
  return `${intro} There is no claims history to show for it.`;
}

function unavailableMessage(lob: LobKey): string {
  return `The claims history configuration for ${LOB_LABELS[lob]} is unavailable.`;
}

function rejectedMessage(lob: LobKey): string {
  const label = LOB_LABELS[lob];
  return `The claims history configuration for ${label} was rejected for the following reasons:`;
}

/**
 * Separate from `unavailableMessage` because the two are diagnosed differently:
 * a missing config is a configuration gap, and this is a config that exists but
 * could not be read.
 */
function retrievalMessage(lob: LobKey): string {
  return `The claims history configuration for ${LOB_LABELS[lob]} could not be loaded.`;
}

/**
 * An in-app link rather than a router link, matching how the landing page and
 * the error pages already link across the application.
 */
function LandingPageLink(): React.ReactElement {
  return (
    <Link
      href={ROUTES.LANDING_PAGE}
      variant="primary"
      ariaLabel="Go to the landing page to choose another line of business"
    >
      Go to the landing page
    </Link>
  );
}

/**
 * The configuration failure surface, rendered in place of the search bar and the
 * table. A page showing this panel has issued no claims request and holds no
 * control that could issue one.
 *
 * No failure offers a retry: against a bundled config every one of them returns
 * the same result on a second attempt, so a retry control would replay the
 * failure while looking as though it had done something. A retry arrives with
 * the backend operation, where a retrieval can fail transiently.
 */
function ConfigErrorPanel({ failure }: ConfigErrorPanelProps): React.ReactElement {
  switch (failure.kind) {
    case "unknownRoute":
      return (
        <Alert
          type="error"
          header="Claims history is unavailable for this page"
          statusIconAriaLabel="Error"
          data-testid="config-error-unknown-route"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p">{unknownRouteMessage(failure.pathname)}</Box>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );

    case "missingConfig":
      return (
        <Alert
          type="error"
          header="Claims history configuration unavailable"
          statusIconAriaLabel="Error"
          data-testid="config-error-missing-config"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p">{unavailableMessage(failure.lob)}</Box>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );

    case "invalidConfig":
      return (
        <Alert
          type="error"
          header="Claims history configuration could not be used"
          statusIconAriaLabel="Error"
          data-testid="config-error-invalid-config"
        >
          <SpaceBetween direction="vertical" size="s">
            <TextContent>
              <div>{rejectedMessage(failure.lob)}</div>
              <ul data-testid="config-error-validation-list">
                {failure.errors.map((error, index) => (
                  <li key={`${error.path}-${index}`}>
                    <b>{`${error.path}: `}</b>
                    {error.message}
                  </li>
                ))}
              </ul>
            </TextContent>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );

    case "retrieval":
      return (
        <Alert
          type="error"
          header="Claims history configuration unavailable"
          statusIconAriaLabel="Error"
          data-testid="config-error-retrieval"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p">{retrievalMessage(failure.lob)}</Box>
            <LandingPageLink />
          </SpaceBetween>
        </Alert>
      );
  }
}

export default ConfigErrorPanel;
